import { type ColumnDef } from "@tanstack/react-table";
import { Spinner } from "@via/components/loading/loading.tsx";
import { HorizontalTabs } from "@via/components/tabs.tsx";
import TableContextMenu from "@via/components/table/table-context-menu.tsx";
import Table from "@via/components/table/table.tsx";
import {
  useAdminTagList,
  useCreateTag,
  useLoadPredefinedTags,
  useUpdateTag,
} from "@via/pages/account/admin/tags/tags.api.tsx";
import TagFormModal from "@via/pages/account/admin/tags/tag-form-modal.tsx";
import { formatDateString } from "@via/utils/helpers.tsx";
import type { Tab, TableContextMenuInterface } from "@via/utils/interfaces.tsx";
import { TaggableEntityType, TagResponse } from "common";
import { type JSX, useCallback, useMemo, useState } from "react";

const TAG_ACTIONS = {
  EDIT: "Edit Tag",
  ACTIVATE: "Activate",
  DEACTIVATE: "Deactivate",
} as const;

const ENTITY_TYPE_LABELS: Record<TaggableEntityType, string> = {
  [TaggableEntityType.Run]: "Runs",
  [TaggableEntityType.Project]: "Projects",
  [TaggableEntityType.Pipeline]: "Pipelines",
  [TaggableEntityType.Dataset]: "Datasets",
  [TaggableEntityType.Process]: "Processes",
};

const ENTITY_TYPE_SINGULAR: Record<TaggableEntityType, string> = {
  [TaggableEntityType.Run]: "Run",
  [TaggableEntityType.Project]: "Project",
  [TaggableEntityType.Pipeline]: "Pipeline",
  [TaggableEntityType.Dataset]: "Dataset",
  [TaggableEntityType.Process]: "Process",
};

const ENTITY_TYPE_TABS: Tab<string>[] = Object.entries(ENTITY_TYPE_LABELS).map(
  ([, label]) => ({ name: label })
);

const labelToEntityType = Object.fromEntries(
  Object.entries(ENTITY_TYPE_LABELS).map(([key, label]) => [label, key])
) as Record<string, TaggableEntityType>;

export default function Tags(): JSX.Element {
  const [activeTab, setActiveTab] = useState(
    ENTITY_TYPE_LABELS[TaggableEntityType.Run]
  );
  const activeEntityType = labelToEntityType[activeTab];

  const { data, isLoading } = useAdminTagList(activeEntityType);
  const { mutate: createTag, isPending: isCreating } = useCreateTag();
  const { mutate: updateTag, isPending: isUpdating } = useUpdateTag();
  const { mutate: loadPredefined, isPending: isLoadingPredefined } =
    useLoadPredefinedTags();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [tagToEdit, setTagToEdit] = useState<TagResponse | null>(null);

  const handleCreate = () => {
    setTagToEdit(null);
    setIsModalOpen(true);
  };

  const handleEdit = (tag: TagResponse) => {
    setTagToEdit(tag);
    setIsModalOpen(true);
  };

  const handleModalSubmit = (formData: { name: string; color?: string }) => {
    if (tagToEdit) {
      updateTag(
        { tagId: tagToEdit.id, ...formData },
        { onSuccess: () => setIsModalOpen(false) }
      );
    } else {
      createTag(
        { ...formData, entityType: activeEntityType },
        { onSuccess: () => setIsModalOpen(false) }
      );
    }
  };

  const getTagMenuOptions = (tag: TagResponse): TableContextMenuInterface[] => [
    { name: TAG_ACTIONS.EDIT, href: "#" },
    {
      name: tag.isActive ? TAG_ACTIONS.DEACTIVATE : TAG_ACTIONS.ACTIVATE,
      href: "#",
    },
  ];

  const handleAction = useCallback(
    (option: TableContextMenuInterface, tag: TagResponse) => {
      switch (option.name) {
        case TAG_ACTIONS.EDIT:
          handleEdit(tag);
          break;
        case TAG_ACTIONS.ACTIVATE:
          updateTag({ tagId: tag.id, isActive: true });
          break;
        case TAG_ACTIONS.DEACTIVATE:
          updateTag({ tagId: tag.id, isActive: false });
          break;
      }
    },
    [updateTag]
  );

  const columns: ColumnDef<TagResponse>[] = useMemo(
    () => [
      {
        id: "color",
        header: () => "Color",
        cell: ({ row }) => (
          <span
            className="inline-block h-5 w-5 rounded-full"
            style={{ backgroundColor: row.original.color ?? "#6B7280" }}
          />
        ),
        size: 70,
      },
      {
        id: "name",
        header: () => "Name",
        cell: ({ row }) => (
          <span className="text-sm font-medium text-gray-900">
            {row.original.name}
          </span>
        ),
        size: 180,
      },
      {
        id: "status",
        header: () => "Status",
        cell: ({ row }) => {
          const isActive = row.original.isActive;
          return (
            <span
              className={`inline-flex items-center whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium ${
                isActive
                  ? "bg-green-100 text-green-800"
                  : "bg-gray-100 text-gray-800"
              }`}>
              {isActive ? "Active" : "Inactive"}
            </span>
          );
        },
        size: 90,
      },
      {
        id: "dateCreated",
        header: () => "Created",
        cell: ({ row }) => (
          <span className="text-sm text-gray-500">
            {formatDateString(row.original.dateCreated, "MMM D, YYYY")}
          </span>
        ),
        size: 120,
      },
      {
        id: "options",
        header: () => "Options",
        cell: ({ row }) => {
          const options = getTagMenuOptions(row.original);
          return (
            <div>
              <TableContextMenu
                options={options}
                onSelect={option => handleAction(option, row.original)}
              />
            </div>
          );
        },
        size: 80,
      },
    ],
    [handleAction]
  );

  const tags = data?.data ?? [];

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-lg font-semibold text-gray-900">Tags</h2>
        <p className="mt-1 text-sm text-gray-500">
          View and manage tags scoped to each entity type
        </p>
      </div>

      <HorizontalTabs
        buttonIdPrefix="admin-tags"
        options={ENTITY_TYPE_TABS}
        activeTab={activeTab}
        onTabChange={setActiveTab}
      />

      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={() => loadPredefined({ entityType: activeEntityType })}
          disabled={isLoadingPredefined}
          className="inline-flex items-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50">
          {isLoadingPredefined ? (
            <Spinner loading={isLoadingPredefined}>Loading...</Spinner>
          ) : (
            `Load Predefined ${ENTITY_TYPE_SINGULAR[activeEntityType]} Tags`
          )}
        </button>
        <button
          type="button"
          onClick={handleCreate}
          className="inline-flex items-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500">
          {`Create ${ENTITY_TYPE_SINGULAR[activeEntityType]} Tag`}
        </button>
      </div>

      <Table
        data={tags}
        columns={columns}
        isLoading={isLoading}
        enableSorting={false}
        total={tags?.length || 0}
        emptyMessage={`No ${ENTITY_TYPE_SINGULAR[activeEntityType].toLowerCase()} tags found. Create one or load predefined tags.`}
      />

      <TagFormModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSubmit={handleModalSubmit}
        tag={tagToEdit}
        isPending={isCreating || isUpdating}
      />
    </div>
  );
}
